<template>
	<div class="ce-upgrade-wrap">
		<!-- LIVE CLIENT, no upgrade yet → create an upgrade copy -->
		<button
			v-if="!isUpgrade && !hasUpgrade"
			class="ce-action-card upgrade-card"
			:class="{ 'ce-action-card--disabled': loading }"
			:disabled="loading"
			type="button"
			@click="openCreate"
		>
			<div class="ce-action-icon upgrade-card__icon upgrade-card__icon--idle">
				<font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': loading }" />
			</div>
			<span class="ce-action-card__text">
				<span class="ce-action-label">Create Upgrade</span>
				<span class="ce-action-desc">{{ loading ? (progress || 'Creating…') : 'Create a copy to redesign' }}</span>
			</span>
			<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
		</button>

		<!-- LIVE CLIENT, upgrade ready → open the upgrade copy -->
		<button
			v-else-if="!isUpgrade && hasUpgrade"
			class="ce-action-card upgrade-card upgrade-card--ready"
			:disabled="loading"
			type="button"
			@click="goToUpgrade"
		>
			<div class="ce-action-icon upgrade-card__icon upgrade-card__icon--ready">
				<font-awesome-icon :icon="['fas', 'external-link-alt']" />
			</div>
			<span class="ce-action-card__text">
				<span class="ce-action-label">Open Upgrade</span>
				<span class="ce-action-desc">Edit the upgrade copy</span>
			</span>
			<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
			<div class="upgrade-card__pulse"></div>
		</button>

		<!-- EDITING THE UPGRADE COPY → Live Client + Apply Upgrade cards -->
		<template v-else>
			<button
				class="ce-action-card upgrade-card upgrade-card--live"
				:disabled="loading"
				type="button"
				@click="goToLive"
			>
				<div class="ce-action-icon upgrade-card__icon upgrade-card__icon--live">
					<font-awesome-icon :icon="['fas', 'arrow-left']" />
				</div>
				<span class="ce-action-card__text">
					<span class="ce-action-label">Live Client</span>
					<span class="ce-action-desc">{{ liveClient ? liveClient.name : 'Back to live client' }}</span>
				</span>
				<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
			</button>

			<button
				class="ce-action-card upgrade-card upgrade-card--apply"
				:disabled="loading"
				type="button"
				@click="goToLaunch"
			>
				<div class="ce-action-icon upgrade-card__icon upgrade-card__icon--apply">
					<font-awesome-icon :icon="['fas', 'rocket']" />
				</div>
				<span class="ce-action-card__text">
					<span class="ce-action-label">Apply Upgrade</span>
					<span class="ce-action-desc">Review changes &amp; go live</span>
				</span>
				<font-awesome-icon :icon="['fas', 'chevron-right']" class="ce-action-card__arrow" />
			</button>
		</template>

		<!-- Create upgrade: confirm → progress, in one modal -->
		<div v-if="createModal.active" class="cu-modal-backdrop">
			<div class="cu-modal">
				<!-- STEP 1 — confirm -->
				<template v-if="createModal.phase === 'confirm'">
					<div class="cu-modal__icon"><font-awesome-icon :icon="['fas', 'question-circle']" /></div>
					<h5 class="cu-modal__title">Create an upgrade copy?</h5>
					<p class="cu-modal__msg">
						A complete copy of this client is created so you can redesign or migrate
						content safely — without touching the live site.
					</p>
					<ul class="cu-list">
						<li><font-awesome-icon :icon="['fas', 'check']" /> Clones pages, landing pages, news, FAQs, departments, posts, files, menus, forms, options &amp; theme</li>
						<li><font-awesome-icon :icon="['fas', 'check']" /> Live site stays untouched — visitors keep seeing the current site</li>
						<li><font-awesome-icon :icon="['fas', 'check']" /> A dedicated staging URL + Plesk subscription is provisioned</li>
						<li><font-awesome-icon :icon="['fas', 'check']" /> Applying later swaps it onto live; the old content is archived</li>
					</ul>
					<div class="cu-modal__actions">
						<button class="j-action-btn cu-btn" @click="closeCreate">Cancel</button>
						<button class="j-action-btn j-action-btn--primary cu-btn" @click="runCreate">
							<font-awesome-icon :icon="['fas', 'sync']" class="me-2" /> Create upgrade
						</button>
					</div>
				</template>

				<!-- STEP 2 — working -->
				<template v-else-if="createModal.phase === 'working'">
					<div class="cu-modal__icon"><span class="spinner-border text-primary" role="status"></span></div>
					<h5 class="cu-modal__title">Creating upgrade…</h5>
					<p class="cu-modal__msg">{{ progress || 'Working…' }}</p>
				</template>

				<!-- STEP 3 — done -->
				<template v-else-if="createModal.phase === 'done'">
					<div class="cu-modal__icon cu-modal__icon--ok"><font-awesome-icon :icon="['fas', 'check-circle']" /></div>
					<h5 class="cu-modal__title">Upgrade copy created</h5>
					<p class="cu-modal__msg">{{ createModal.message }}</p>
					<div class="cu-modal__actions">
						<button class="j-action-btn cu-btn" @click="closeCreate">Close</button>
						<button class="j-action-btn j-action-btn--primary cu-btn" @click="openCreatedUpgrade">Open Upgrade</button>
					</div>
				</template>

				<!-- STEP 4 — failed -->
				<template v-else>
					<div class="cu-modal__icon cu-modal__icon--warn"><font-awesome-icon :icon="['fas', 'exclamation-triangle']" /></div>
					<h5 class="cu-modal__title">Upgrade failed</h5>
					<p class="cu-modal__msg">{{ createModal.error }}</p>
					<div class="cu-modal__actions">
						<button class="j-action-btn cu-btn" @click="closeCreate">Close</button>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'
import restApi from '@/api/index'

export default {
	name: 'JuniperClientUpgradeCard',
	props: {
		clientId: { type: [Number, String], required: true },
	},
	data() {
		return {
			loading: false,
			progress: '',
			progressTimer: null,
			hasUpgrade: false,
			isUpgrade: false,
			upgrade: null,
			liveClient: null,
			recentArchives: [],
			createModal: { active: false, phase: 'confirm', message: '', error: '' },
		}
	},
	mounted() {
		this.fetchStatus()
	},
	beforeDestroy() {
		this.stopProgressPolling()
	},
	watch: {
		clientId(newId, oldId) {
			if (newId && newId !== oldId) {
				this.hasUpgrade = false
				this.isUpgrade = false
				this.upgrade = null
				this.liveClient = null
				this.recentArchives = []
				this.fetchStatus()
			}
		},
	},
	methods: {
		async fetchStatus() {
			try {
				const { data } = await juniperApi.get(`/clients/${this.clientId}/upgrade/status`)
				this.hasUpgrade = !!data.has_upgrade
				this.isUpgrade = !!data.is_upgrade
				this.upgrade = data.upgrade ?? null
				this.liveClient = data.live_client ?? null
				this.recentArchives = data.recent_archives ?? []
				this.$emit('upgrade-status', {
					hasUpgrade: this.hasUpgrade,
					isUpgrade: this.isUpgrade,
					upgrade: this.upgrade,
					liveClient: this.liveClient,
					recentArchives: this.recentArchives,
				})
			} catch (err) {
				console.error('Failed to fetch upgrade status', err)
			}
		},

		// --- Navigation ----------------------------------------------------
		navigate(target) {
			if (this.$route.path === target) return
			this.$router.push(target).catch(err => {
				if (err.name !== 'NavigationDuplicated') {
					console.error('Navigation failed', err)
				}
			})
		},
		goToLive() {
			if (this.liveClient?.id) this.navigate(`/juniper/clients/edit/${this.liveClient.id}`)
		},
		goToUpgrade() {
			if (this.upgrade?.id) this.navigate(`/juniper/clients/edit/${this.upgrade.id}`)
		},
		goToLaunch() {
			// Apply / Discard / diff all live on the upgrade-launch screen.
			// The route resolves the pair from either side; we pass the live id.
			const id = this.liveClient?.id || this.clientId
			this.navigate(`/juniper/clients/${id}/upgrade-launch`)
		},

		// --- Create upgrade ------------------------------------------------
		openCreate() {
			this.createModal.active = true
			this.createModal.phase = 'confirm'
			this.createModal.message = ''
			this.createModal.error = ''
		},
		closeCreate() {
			this.createModal.active = false
		},
		openCreatedUpgrade() {
			this.createModal.active = false
			this.goToUpgrade()
		},

		async runCreate() {
			this.createModal.phase = 'working'
			this.loading = true
			this.progress = 'Creating upgrade…'
			this.startProgressPolling()
			try {
				// 1. Create the upgrade copy in juniper-api (long-running; backend writes progress to cache)
				const { data: cloneResp } = await juniperApi.post(`/clients/${this.clientId}/upgrade`)
				const upgrade = cloneResp.upgrade
				this.stopProgressPolling()

				// 2. Provision Plesk subscription for the upgrade (mc2-api).
				//    mc2-api returns ftp_user / ftp_password — we then write them
				//    onto the upgrade client in juniper-api, mirroring the normal
				//    client-creation flow in ClientNew.vue.
				if (upgrade?.id && upgrade?.url && upgrade?.staging_url) {
					try {
						this.progress = 'Provisioning Plesk subscription…'
						const { data: provResp } = await restApi.post('/juniper/client/upgrade/subscription', {
							name: upgrade.name,
							mc_client_id: cloneResp.live_client_id,
							juniper_id: upgrade.id,
							url: upgrade.url,
							staging_url: upgrade.staging_url,
						})

						if (provResp?.ftp_user && provResp?.ftp_password) {
							this.progress = 'Saving FTP credentials…'
							await juniperApi.post(`/clients/${upgrade.id}/credentials`, {
								ftp_user: provResp.ftp_user,
								ftp_password: provResp.ftp_password,
							})
						}
					} catch (provErr) {
						console.error('Plesk provisioning failed', provErr)
						const provMsg = provErr?.response?.data?.error ?? provErr.message ?? 'You can retry provisioning manually.'
						await this.fetchStatus()
						this.$emit('upgrade-changed')
						this.createModal.phase = 'done'
						this.createModal.message = `Upgrade copy created, but Plesk provisioning failed: ${provMsg}`
						return
					}
				}

				await this.fetchStatus()
				this.$emit('upgrade-changed')
				this.createModal.phase = 'done'
				this.createModal.message = 'The upgrade copy is ready. You can open it to start redesigning.'
			} catch (err) {
				this.createModal.phase = 'failed'
				this.createModal.error = err?.response?.data?.message ?? err.message ?? 'Upgrade failed.'
			} finally {
				this.stopProgressPolling()
				this.progress = ''
				this.loading = false
			}
		},

		startProgressPolling() {
			this.stopProgressPolling()
			this.progressTimer = setInterval(async () => {
				try {
					const { data } = await juniperApi.get(`/clients/${this.clientId}/upgrade/progress`)
					if (data?.message) {
						this.progress = data.message
					}
				} catch (e) {
					// Tihi fail — endpoint nije kritičan
				}
			}, 1500)
		},

		stopProgressPolling() {
			if (this.progressTimer) {
				clearInterval(this.progressTimer)
				this.progressTimer = null
			}
		},
	},
}
</script>

<style scoped>
.ce-upgrade-wrap {
	display: contents;
}

/* === Card (horizontal list row, matches ClientEdit .ce-action-card) === */
.ce-action-card.upgrade-card {
	background: #fff;
	border-radius: 14px;
	padding: 11px 13px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
	text-decoration: none;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;
	text-align: left;
	transition: box-shadow 0.15s, transform 0.12s, border-color 0.15s;
	border: 1px solid #eef0f2;
	position: relative;
	cursor: pointer;
	font: inherit;
	color: inherit;
	width: 100%;
	overflow: hidden;
}
.ce-action-card.upgrade-card:hover {
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
	transform: translateY(-1px);
	border-color: #dbe3ee;
}
.upgrade-card .ce-action-card__text {
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
}
.upgrade-card .ce-action-desc {
	font-size: 11px;
	color: #9ca3af;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-top: 1px;
}
.upgrade-card .ce-action-card__arrow {
	font-size: 11px;
	color: #cbd5e1;
	opacity: 0;
	flex-shrink: 0;
	transition: opacity 0.15s ease, transform 0.15s ease;
}
.upgrade-card:hover .ce-action-card__arrow {
	opacity: 1;
	transform: translateX(2px);
}
.upgrade-card--ready {
	background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
	border-color: #86efac !important;
}
.upgrade-card--ready:hover {
	border-color: #16a34a !important;
}
.upgrade-card--live {
	background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
	border-color: #93c5fd !important;
}
.upgrade-card--live:hover {
	border-color: #2563eb !important;
}
.upgrade-card--apply {
	background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
	border-color: #86efac !important;
}
.upgrade-card--apply:hover {
	border-color: #16a34a !important;
}
.ce-action-card--disabled {
	opacity: 0.5;
	pointer-events: none;
}

.upgrade-card__icon {
	width: 40px;
	height: 40px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	flex-shrink: 0;
	transition: transform 0.2s;
}
.upgrade-card__icon--idle {
	background: #ffedd5;
	color: #c2410c;
}
.upgrade-card__icon--ready {
	background: #dcfce7;
	color: #166534;
}
.upgrade-card__icon--live {
	background: #dbeafe;
	color: #1d4ed8;
}
.upgrade-card__icon--apply {
	background: #bbf7d0;
	color: #15803d;
}
.upgrade-card:hover .upgrade-card__icon {
	transform: scale(1.05);
}

.ce-action-label {
	font-size: 13px;
	font-weight: 600;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.upgrade-card__pulse {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #16a34a;
	box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.7);
	animation: upgrade-pulse 2s infinite;
}
@keyframes upgrade-pulse {
	0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.7); }
	70% { box-shadow: 0 0 0 8px rgba(22, 163, 74, 0); }
	100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
}

/* === Create-upgrade modal === */
.cu-modal-backdrop {
	position: fixed; inset: 0; z-index: 1080;
	background: rgba(15, 23, 42, 0.45);
	display: flex; align-items: center; justify-content: center; padding: 20px;
}
.cu-modal {
	background: #fff; border-radius: 16px; padding: 26px 26px; width: 100%; max-width: 460px;
	text-align: center; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
.cu-modal__icon { font-size: 34px; margin-bottom: 10px; color: #64748b; }
.cu-modal__icon--ok { color: #16a34a; }
.cu-modal__icon--warn { color: #d97706; }
.cu-modal__title { font-weight: 700; margin: 0 0 6px; }
.cu-modal__msg { color: #4b5563; font-size: 14px; margin: 0 0 14px; }
.cu-list {
	list-style: none; padding: 0; margin: 0 0 18px; text-align: left;
	display: flex; flex-direction: column; gap: 8px;
}
.cu-list li { font-size: 13px; color: #374151; display: flex; gap: 8px; align-items: flex-start; }
.cu-list li > svg { color: #16a34a; margin-top: 3px; flex-shrink: 0; }
.cu-modal__actions { display: flex; justify-content: center; gap: 10px; }
.cu-btn { height: 40px; font-size: 14px; font-weight: 600; padding: 0 20px; border-radius: 22px; }
</style>
